<script setup lang="ts">
import {
  ScalarIconCaretDown,
  ScalarIconCheck,
  ScalarIconTranslate,
} from '@scalar/icons'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { useI18n } from '@/i18n'

const props = withDefaults(
  defineProps<{
    placement?: 'top' | 'bottom'
  }>(),
  {
    placement: 'bottom',
  },
)

const selectedLocale = defineModel<string>({ required: true })
const { t } = useI18n()

const root = ref<HTMLElement | null>(null)
const isOpen = ref(false)

const languageOptions = [
  {
    locale: 'en',
    label: 'English',
    shortLabel: 'EN',
  },
  {
    locale: 'zh-CN',
    label: '简体中文',
    shortLabel: '中',
  },
] as const

const normalizeBuiltInLocale = (value: string) =>
  value.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en'

const currentLocale = computed(() => normalizeBuiltInLocale(selectedLocale.value))

const currentLanguage = computed(
  () =>
    languageOptions.find((option) => option.locale === currentLocale.value) ??
    languageOptions[0],
)

function toggleMenu() {
  isOpen.value = !isOpen.value
}

function selectLanguage(locale: string) {
  selectedLocale.value = locale
  isOpen.value = false
}

function handleDocumentPointerDown(event: MouseEvent) {
  if (!root.value?.contains(event.target as Node)) {
    isOpen.value = false
  }
}

function handleDocumentKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleDocumentPointerDown)
  document.addEventListener('keydown', handleDocumentKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleDocumentPointerDown)
  document.removeEventListener('keydown', handleDocumentKeydown)
})
</script>

<template>
  <div
    ref="root"
    class="reference-language-select"
    :class="`reference-language-select-${props.placement}`">
    <button
      class="reference-language-select-trigger"
      type="button"
      :aria-expanded="isOpen"
      :aria-label="t('language.selectLanguage')"
      aria-haspopup="menu"
      @click="toggleMenu">
      <ScalarIconTranslate
        aria-hidden="true"
        class="reference-language-select-icon" />
      <span class="reference-language-select-current">
        {{ currentLanguage.shortLabel }}
      </span>
      <ScalarIconCaretDown
        aria-hidden="true"
        class="reference-language-select-caret"
        :class="{ 'reference-language-select-caret-open': isOpen }" />
    </button>

    <Transition
      enterActiveClass="reference-language-select-menu-enter-active"
      enterFromClass="reference-language-select-menu-enter-from"
      enterToClass="reference-language-select-menu-enter-to"
      leaveActiveClass="reference-language-select-menu-leave-active"
      leaveFromClass="reference-language-select-menu-leave-from"
      leaveToClass="reference-language-select-menu-leave-to">
      <div
        v-if="isOpen"
        class="reference-language-select-menu"
        role="menu">
        <button
          v-for="option in languageOptions"
          :key="option.locale"
          class="reference-language-select-option"
          :class="{
            'reference-language-select-option-active':
              option.locale === currentLocale,
          }"
          type="button"
          :aria-current="option.locale === currentLocale ? 'true' : undefined"
          role="menuitemradio"
          :aria-checked="option.locale === currentLocale"
          @click="selectLanguage(option.locale)">
          <span>{{ option.label }}</span>
          <ScalarIconCheck
            v-if="option.locale === currentLocale"
            aria-hidden="true"
            class="reference-language-select-check" />
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.reference-language-select {
  position: relative;
  display: inline-flex;
}

.reference-language-select-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 40px;
  height: 25px;
  padding: 0 3px;
  border: var(--scalar-border-width) solid var(--scalar-border-color);
  border-radius: var(--scalar-radius);
  background: var(--scalar-background-1);
  color: var(--scalar-color-2);
  font-size: var(--scalar-font-size-2);
  font-weight: var(--scalar-semibold);
  line-height: 1;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}

.reference-language-select-trigger:hover,
.reference-language-select-trigger[aria-expanded='true'] {
  background: var(--scalar-background-2);
  color: var(--scalar-color-1);
}

.reference-language-select-icon {
  width: 12px;
  height: 12px;
}

.reference-language-select-current {
  min-width: 18px;
  text-align: center;
  font-size: 12px;
}

.reference-language-select-caret {
  width: 10px;
  height: 10px;
  transition: transform 0.15s ease;
}

.reference-language-select-caret-open {
  transform: rotate(180deg);
}

.reference-language-select-menu {
  position: absolute;
  right: 0;
  z-index: 30;
  min-width: 148px;
  padding: 4px;
  border: var(--scalar-border-width) solid var(--scalar-border-color);
  border-radius: var(--scalar-radius-lg);
  background: var(--scalar-background-1);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--scalar-color-1) 14%, transparent);
}

.reference-language-select-bottom .reference-language-select-menu {
  top: calc(100% + 6px);
}

.reference-language-select-top .reference-language-select-menu {
  bottom: calc(100% + 6px);
}

.reference-language-select-option {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 30px;
  padding: 0 8px;
  border: 0;
  border-radius: var(--scalar-radius);
  background: transparent;
  color: var(--scalar-color-2);
  font-size: 12px;
  text-align: left;
  white-space: nowrap;
}

.reference-language-select-option:hover,
.reference-language-select-option-active {
  background: var(--scalar-background-2);
  color: var(--scalar-color-1);
}

.reference-language-select-check {
  width: 14px;
  height: 14px;
  color: var(--scalar-color-1);
}

.reference-language-select-menu-enter-active,
.reference-language-select-menu-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.reference-language-select-menu-enter-from,
.reference-language-select-menu-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}

.reference-language-select-menu-enter-to,
.reference-language-select-menu-leave-from {
  opacity: 1;
  transform: translateY(0);
}

:global(.darklight-reference) .reference-language-select-trigger {
  border-color: var(--scalar-sidebar-border-color);
  background: transparent;
  color: var(--scalar-sidebar-color-2);
}

:global(.darklight-reference) .reference-language-select-trigger:hover,
:global(.darklight-reference)
  .reference-language-select-trigger[aria-expanded='true'] {
  /* background: var(--scalar-sidebar-item-hover-background); */
  color: var(--scalar-sidebar-color-1);
}
</style>
